import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@material-ui/core';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { useCampfireQuery } from '../../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../../global/config/useSnackbar';
import { AdHocRuleDialog } from './AdHocRuleDialog';
import {
  GET_AD_HOC_REPORTING_RULES,
  GET_PROGRAMS_FOR_AD_HOC,
  GET_ACTIVITIES_FOR_AD_HOC,
  GET_ACTIVITY_ROLES_FOR_AD_HOC,
} from './ad-hoc-reporting.gql';
import { VOLUNTEER_PermissionLevelEnumType } from '../../../../../__generated__/globalTypes';

interface Program {
  programId: string;
  name: string;
}

interface Activity {
  activityId: string;
  name: string;
  programId?: string;
}

interface ActivityRole {
  activityRoleId: string;
  name: string;
}

interface AdHocReportingRule {
  adHocReportingRuleId: string;
  name: string;
  description?: string;
  isActive: boolean;
  matchType: string;
  programs: Program[];
  activities: Activity[];
  roles: ActivityRole[];
  permissionLevels: string[];
  createdAt: string;
  updatedAt?: string;
}

interface AdHocRulesQueryResponse {
  vm: {
    adHocReportingRules: AdHocReportingRule[];
  };
}

interface ProgramsQueryResponse {
  vm: {
    programs: Program[];
  };
}

interface ActivitiesQueryResponse {
  vm: {
    programs: {
      programId: string;
      activities: Activity[];
    }[];
  };
}

interface RolesQueryResponse {
  vm: {
    activityRoles: ActivityRole[];
  };
}

export function AdHocRuleManagement() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<AdHocReportingRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const campfireFetch = useCampfireFetch({ defer: true });
  const { theme } = useCampfireTheme();

  const { data, loading, refetch } = useCampfireQuery<AdHocRulesQueryResponse, {}>(GET_AD_HOC_REPORTING_RULES, {});
  const { data: programsData } = useCampfireQuery<ProgramsQueryResponse, {}>(GET_PROGRAMS_FOR_AD_HOC, {});
  const { data: activitiesData } = useCampfireQuery<ActivitiesQueryResponse, {}>(GET_ACTIVITIES_FOR_AD_HOC, {});
  const { data: rolesData } = useCampfireQuery<RolesQueryResponse, {}>(GET_ACTIVITY_ROLES_FOR_AD_HOC, {});

  const rules = data?.vm?.adHocReportingRules || [];
  const programs = programsData?.vm?.programs || [];
  const activities =
    activitiesData?.vm?.programs?.flatMap((p) => p.activities.map((a) => ({ ...a, programId: p.programId }))) || [];
  const roles = rolesData?.vm?.activityRoles || [];

  const permissionLevelOptions = [
    { value: VOLUNTEER_PermissionLevelEnumType.admin, label: 'Admin' },
    { value: VOLUNTEER_PermissionLevelEnumType.pm, label: 'Program Manager' },
    { value: VOLUNTEER_PermissionLevelEnumType.al, label: 'Activity Leader' },
    { value: VOLUNTEER_PermissionLevelEnumType.gv, label: 'General Volunteer' },
  ];

  const handleEdit = (rule: AdHocReportingRule) => {
    setSelectedRule(rule);
    setDialogOpen(true);
  };

  const handleDeleteClick = (ruleId: string) => {
    setRuleToDelete(ruleId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!ruleToDelete) return;

    try {
      await campfireFetch.run({
        url: `${apiUrl}/vm/admin-console/ad-hoc-rules/delete`,
        method: 'post',
        data: { id: ruleToDelete },
      });

      setSnackbar({
        open: true,
        message: 'Rule deleted successfully',
        variant: 'success',
      });

      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error deleting rule',
        variant: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setRuleToDelete(null);
    }
  };

  const handleToggleActive = async (rule: AdHocReportingRule) => {
    try {
      await campfireFetch.run({
        url: `${apiUrl}/vm/admin-console/ad-hoc-rules/save`,
        method: 'post',
        data: {
          id: rule.adHocReportingRuleId,
          name: rule.name,
          description: rule.description || null,
          matchType: rule.matchType,
          isActive: !rule.isActive,
          programIds: rule.programs.map((p) => p.programId),
          activityIds: rule.activities.map((a) => a.activityId),
          roleIds: rule.roles.map((r) => r.activityRoleId),
          permissionLevels: rule.permissionLevels,
        },
      });

      setSnackbar({
        open: true,
        message: `Rule ${!rule.isActive ? 'activated' : 'deactivated'} successfully`,
        variant: 'success',
      });

      refetch();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Error updating rule status',
        variant: 'error',
      });
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedRule(null);
    refetch();
  };

  const getCriteriaDisplay = (rule: AdHocReportingRule) => {
    const criteriaCount =
      rule.programs.length + rule.activities.length + rule.roles.length + rule.permissionLevels.length;
    if (criteriaCount === 0) return 'No criteria';
    return `${criteriaCount} criteria`;
  };

  const getMatchTypeDisplay = (matchType: string) => {
    return matchType === 'all' ? 'Match All' : 'Match Any';
  };

  return (
    <Box mt={3}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={1}>
        <Typography style={{ fontWeight: 600, fontSize: '16px', lineHeight: '24px' }}>
          Ad-Hoc Reporting Rules
        </Typography>
        <Button
          variant='contained'
          onClick={() => {
            setSelectedRule(null);
            setDialogOpen(true);
          }}
          style={{
            textTransform: 'none',
            color: '#ffffff',
            padding: '7px 12px',
            backgroundColor: theme.color.secondary.main900,
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: '19px',
          }}
        >
          <AddIcon style={{ fontSize: '18px', marginRight: '6px' }} />
          Create Rule
        </Button>
      </Box>

      <Typography
        variant='body2'
        style={{ fontSize: '13px', color: '#808080', marginBottom: '16px', lineHeight: '19px' }}
      >
        Define rules to control which volunteers can submit ad-hoc reports for specific activities and programs.
      </Typography>

      <TableContainer component={Paper} style={{ boxShadow: 'none', border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
              <TableCell style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>Rule Name</TableCell>
              <TableCell style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>Description</TableCell>
              <TableCell align='center' style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>
                Match Type
              </TableCell>
              <TableCell align='center' style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>
                Criteria
              </TableCell>
              <TableCell align='center' style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>
                Status
              </TableCell>
              <TableCell align='center' style={{ fontWeight: 600, fontSize: '13px', color: '#424242' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align='center' style={{ padding: '24px' }}>
                  <Typography style={{ fontSize: '13px', color: '#808080' }}>Loading rules...</Typography>
                </TableCell>
              </TableRow>
            ) : rules.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align='center'>
                  <Box py={4}>
                    <Typography style={{ fontSize: '13px', color: '#808080' }}>
                      No rules defined yet. Create your first rule to get started.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              rules.map((rule) => (
                <TableRow key={rule.adHocReportingRuleId} hover>
                  <TableCell>
                    <Typography style={{ fontSize: '13px', fontWeight: 500, color: '#212121' }}>{rule.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography style={{ fontSize: '13px', color: '#616161', maxWidth: 300 }}>
                      {rule.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Chip
                      label={getMatchTypeDisplay(rule.matchType)}
                      size='small'
                      variant='outlined'
                      color={rule.matchType === 'all' ? 'primary' : 'default'}
                      style={{ fontSize: '12px', height: '24px' }}
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Typography style={{ fontSize: '13px', color: '#616161' }}>{getCriteriaDisplay(rule)}</Typography>
                  </TableCell>
                  <TableCell align='center'>
                    <Switch
                      checked={rule.isActive}
                      onChange={() => handleToggleActive(rule)}
                      color='primary'
                      size='small'
                    />
                  </TableCell>
                  <TableCell align='center'>
                    <Box display='flex' justifyContent='center'>
                      <IconButton size='small' onClick={() => handleEdit(rule)} title='Edit rule'>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton
                        size='small'
                        onClick={() => handleDeleteClick(rule.adHocReportingRuleId)}
                        title='Delete rule'
                      >
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {dialogOpen && (
        <AdHocRuleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          rule={selectedRule}
          programs={programs}
          activities={activities}
          roles={roles}
          permissionLevelOptions={permissionLevelOptions}
        />
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} aria-labelledby='delete-dialog-title'>
        <DialogTitle id='delete-dialog-title'>Delete Rule</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this rule? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <TabletButton onClick={() => setDeleteDialogOpen(false)}>Cancel</TabletButton>
          <TabletButton onClick={handleDeleteConfirm} variant='outlined' color='error'>
            Delete
          </TabletButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
